/**
 * AI Chat Service
 * API calls for AI chat conversations and messages
 */

import { apiClient } from "@/lib/api-client"
import type {
  AiConversation,
  AiConversationDetailResponse,
  AiConversationListResponse,
  CreateConversationResponse,
} from "../types/ai-chat.types"

interface ApiResponse<T> {
  success: boolean
  data: T
  message: string
}

export async function createConversation(): Promise<CreateConversationResponse> {
  const response = await apiClient.post<ApiResponse<CreateConversationResponse>>(
    "/api/ai-chat/conversations",
  )
  return response.data.data
}

export async function listConversations(page = 1, limit = 20): Promise<AiConversationListResponse> {
  const response = await apiClient.get<ApiResponse<AiConversationListResponse>>(
    "/api/ai-chat/conversations",
    { params: { page, limit } },
  )
  return response.data.data
}

export async function getConversation(
  id: string,
  messagePage = 1,
  messageLimit = 50,
): Promise<AiConversationDetailResponse> {
  const response = await apiClient.get<ApiResponse<AiConversationDetailResponse>>(
    `/api/ai-chat/conversations/${id}`,
    {
      params: { messagePage, messageLimit },
    },
  )
  return response.data.data
}

export async function updateConversation(
  id: string,
  data: { title?: string; status?: "active" | "archived" },
): Promise<AiConversation> {
  const response = await apiClient.patch<ApiResponse<AiConversation>>(
    `/api/ai-chat/conversations/${id}`,
    data,
  )
  return response.data.data
}

export async function deleteConversation(id: string): Promise<void> {
  await apiClient.delete(`/api/ai-chat/conversations/${id}`)
}

export interface StartCoachChatResult {
  id: string
}

/**
 * Start a Rituality Coach chat for a ritual. Creates a conversation on the
 * backend with the greeting + "things to prepare" assistant messages already
 * persisted, so they survive logout/refresh.
 */
export async function startCoachChat(ritualId: string): Promise<StartCoachChatResult> {
  const response = await apiClient.post<ApiResponse<StartCoachChatResult>>(
    "/api/ai-chat/coach-chat",
    { ritualId },
  )
  return response.data.data
}

export interface OnDemandReflectResult {
  id: string
  created: boolean
}

/**
 * Create or get the on-demand reflect conversation for a completed ritual.
 * Idempotent: repeated clicks of the Reflect button return the same
 * conversation id.
 */
export async function createOnDemandReflect(ritualId: string): Promise<OnDemandReflectResult> {
  const response = await apiClient.post<ApiResponse<OnDemandReflectResult>>(
    "/api/ai-chat/reflect",
    { ritualId },
  )
  return response.data.data
}

/**
 * Returns the SSE endpoint URL for sending messages.
 * Used by the Vercel AI SDK's useChat hook via DefaultChatTransport.
 */
export function getMessageStreamUrl(conversationId: string): string {
  const baseUrl = import.meta.env.VITE_API_URL || ""
  return `${baseUrl}/api/ai-chat/conversations/${conversationId}/messages`
}
